/**
 * Named spawn modes — deliberate local coupling recipes (Track Z).
 */

import type { SpawnPolicy } from "../sim/spawnPolicy";

export type SpawnModeId =
  | "scatter"
  | "join_nearest"
  | "local_pair"
  | "local_random"
  | "local_dipole"
  | "seed_attract"
  | "seed_repel";

export type SpawnModeDef = {
  id: SpawnModeId;
  label: string;
  /** Chip one-liner */
  hint: string;
  summary: string;
  detail: string;
  policy: Omit<SpawnPolicy, "localRadius"> & { localRadius?: number };
};

export const SPAWN_MODES: SpawnModeDef[] = [
  {
    id: "join_nearest",
    label: "Join nearest",
    hint: "click near a body to couple toward it",
    summary: "Friend = nearest agent; enemy = random global.",
    detail:
      "Deliberate default. Place beside structure and the newcomer’s friend pointer locks onto the closest body so motion pulls into that locus. Enemy stays a global random so the cloud still has long-range push.",
    policy: {
      friend: "nearest",
      enemy: "random_global",
      inbound: "none",
      avoidFriendEqEnemy: true,
    },
  },
  {
    id: "local_pair",
    label: "Local pair",
    hint: "nearest friend, next-nearest enemy",
    summary: "Friend = nearest; enemy = second-nearest.",
    detail:
      "Strong local dipole: pull to the closest body and push from the next-closest. Great between two neighbors on a ring or clique.",
    policy: {
      friend: "nearest",
      enemy: "second_nearest",
      inbound: "none",
      avoidFriendEqEnemy: true,
    },
  },
  {
    id: "local_random",
    label: "Local random",
    hint: "lottery inside neighborhood radius",
    summary: "Friend and enemy random among agents within R.",
    detail:
      "Same lottery as scatter, but only inside the local ball (neighbor radius). Empty ball falls back to global random and the chip says so.",
    policy: {
      friend: "random_local",
      enemy: "random_local",
      inbound: "none",
      avoidFriendEqEnemy: true,
    },
  },
  {
    id: "local_dipole",
    label: "Local dipole",
    hint: "pull closest, push far edge of pocket",
    summary: "Friend = nearest; enemy = farthest in local ball.",
    detail:
      "Pull to the nearest agent and push away from the farthest agent still inside radius R — a local stretch/compress feel.",
    policy: {
      friend: "nearest",
      enemy: "farthest_local",
      inbound: "none",
      avoidFriendEqEnemy: true,
    },
  },
  {
    id: "seed_attract",
    label: "Seed attract",
    hint: "nearest also friends you",
    summary: "Join nearest + nearest agent’s friend → newcomer.",
    detail:
      "Outbound: friend = nearest. Inbound: that nearest agent rewires its friend pointer to you — the neighborhood notices and pulls back.",
    policy: {
      friend: "nearest",
      enemy: "random_global",
      inbound: "nearest_friend",
      avoidFriendEqEnemy: true,
    },
  },
  {
    id: "seed_repel",
    label: "Seed repel",
    hint: "nearest fears you",
    summary: "Enemy = nearest; nearest agent’s enemy → newcomer.",
    detail:
      "Inject a local avoid target: your enemy is the closest body, and that body also marks you as enemy — mutual local repulsion seed.",
    policy: {
      friend: "random_global",
      enemy: "nearest",
      inbound: "nearest_enemy",
      avoidFriendEqEnemy: true,
    },
  },
  {
    id: "scatter",
    label: "Scatter",
    hint: "classic random friend & enemy",
    summary: "Both friend and enemy uniform random (legacy).",
    detail:
      "Pre-Z baseline. Geometry is local (click position) but relationships are a global lottery — useful as noise, weak for deliberate local structure.",
    policy: {
      friend: "random_global",
      enemy: "random_global",
      inbound: "none",
      avoidFriendEqEnemy: true,
    },
  },
];

export const DEFAULT_SPAWN_MODE: SpawnModeId = "join_nearest";

export function getSpawnMode(id: string | null | undefined): SpawnModeDef {
  return SPAWN_MODES.find((m) => m.id === id) ?? SPAWN_MODES[0]!;
}

export function spawnPolicyForMode(
  mode: SpawnModeDef,
  neighborRadius: number,
): SpawnPolicy {
  const R = Math.max(0.15, neighborRadius > 0 ? neighborRadius : 0.35);
  return {
    friend: mode.policy.friend,
    enemy: mode.policy.enemy,
    inbound: mode.policy.inbound,
    avoidFriendEqEnemy: mode.policy.avoidFriendEqEnemy !== false,
    localRadius: mode.policy.localRadius ?? R,
  };
}
